import { useEffect, useRef, useState } from "react";
import { isEqual } from "lodash";
import {
  Box,
  Grid,
  ListItem,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress
} from "@material-ui/core";
import matchSubstrings from "autosuggest-highlight/match";
import parseSubstrings from "autosuggest-highlight/parse";
import { useTranslation } from "react-i18next";
import useWebotServer from "hooks/useWebotServer";
import type { FocusEvent, SyntheticEvent } from "react";
import type {
  REST_schema,
  ProcessedListingAddress,
  ProcessedListingAddressCoordinates
} from "../../../../@types/sharedSchema";

type LocationSuggestion = REST_schema.LocationSuggestion;
type AddressLevel = REST_schema.LocationSuggestionLevel;

/**
 * The three-field address entry for the listing dialog: עיר / שכונה / רחוב as
 * cross-synced autocompletes over the canonical location registry, plus the
 * interactive map in a collapsed accordion.
 *
 * Contract with the dialog: emits the same single-entry
 * `ProcessedListingAddress[]` the old single LocationPicker emitted
 * (`{city, neighborhood, street, coords?, canonicalLocationId?, canonicalLevel?}`),
 * so `createOrUpdateListing` keeps destructuring `address[0]` unchanged.
 *
 * Cross-sync rules (all free-text friendly — a place missing from the registry
 * must never block listing creation):
 * - City pick scopes neighborhood/street suggestions (`cityCode`); moving to a
 *   DIFFERENT city clears neighborhood/street only when they are known to
 *   belong to another city. Clearing the city keeps them and just un-scopes.
 * - Neighborhood/street picks fill the city when it is empty (suggestions
 *   carry their city).
 * - A street pick fills an empty neighborhood: directly when the suggestion
 *   carries one (expandStreetNeighborhoods — future server), otherwise via the
 *   corpus inference endpoint (best-effort, silently skipped on null/error).
 */

type AddressDraft = {
  city: string;
  neighborhood: string;
  street: string;
  /** Canonical city code — scopes neighborhood/street suggestion queries. */
  cityCode?: number;
  cityLocationId?: string;
  neighborhoodLocationId?: string;
  streetLocationId?: string;
  /** Which city each value is KNOWN to belong to (from its suggestion, or the
   *  city field at commit time). Undefined = unknown (free text with no city) —
   *  unknown values are never cleared by a later city change. */
  neighborhoodCity?: string;
  streetCity?: string;
  coords?: ProcessedListingAddressCoordinates;
};

type AddressFieldsPickerProps = {
  /** Applied to the city field — form-level validation still blocks save without a city. */
  required?: boolean;
  disabled?: boolean;
  /** The dialog's address state: 0 or 1 entries. */
  address: ProcessedListingAddress[];
  onAddressChange: (address: ProcessedListingAddress[]) => void;
};

const sameText = (a?: string, b?: string): boolean => (a ?? "").trim() === (b ?? "").trim();

function fromAddress(address?: ProcessedListingAddress[]): AddressDraft {
  const entry = address?.[0];

  const draft: AddressDraft = {
    city: entry?.city ?? "",
    neighborhood: entry?.neighborhood ?? "",
    street: entry?.street ?? "",
    coords: entry?.coords,
    // Values seeded together (edit mode) belong to the seeded city, so a later
    // city change correctly clears them.
    neighborhoodCity: entry?.neighborhood ? entry?.city?.trim() || undefined : undefined,
    streetCity: entry?.street ? entry?.city?.trim() || undefined : undefined
  };

  if (entry?.canonicalLocationId) {
    if (entry.canonicalLevel === "street") draft.streetLocationId = entry.canonicalLocationId;
    else if (entry.canonicalLevel === "neighborhood")
      draft.neighborhoodLocationId = entry.canonicalLocationId;
    else if (entry.canonicalLevel === "city") draft.cityLocationId = entry.canonicalLocationId;
  }

  return draft;
}

function toAddress(draft: AddressDraft): ProcessedListingAddress[] {
  if (!draft.city && !draft.neighborhood && !draft.street) return [];

  // The canonical id of the MOST SPECIFIC picked field rides the address, so
  // downstream id-based matching gets the best anchor we have.
  const canonical =
    draft.street && draft.streetLocationId
      ? { canonicalLocationId: draft.streetLocationId, canonicalLevel: "street" as const }
      : draft.neighborhood && draft.neighborhoodLocationId
      ? { canonicalLocationId: draft.neighborhoodLocationId, canonicalLevel: "neighborhood" as const }
      : draft.city && draft.cityLocationId
      ? { canonicalLocationId: draft.cityLocationId, canonicalLevel: "city" as const }
      : {};

  return [
    {
      city: draft.city,
      neighborhood: draft.neighborhood,
      street: draft.street,
      ...(draft.coords ? { coords: draft.coords } : {}),
      ...canonical
    }
  ];
}

export function AddressFieldsPicker({
  required,
  disabled,
  address,
  onAddressChange
}: AddressFieldsPickerProps): JSX.Element {
  const api = useWebotServer();
  const { t: translate } = useTranslation();

  const [draft, setDraft] = useState<AddressDraft>(() => fromAddress(address));

  // Mirror for async callbacks (neighborhood inference) — they must read the
  // CURRENT draft, not the one captured when the request started.
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // What we last handed to the parent — lets us tell an echo of our own change
  // apart from an external re-seed (edit-mode reopen), which we must adopt.
  const lastEmittedRef = useRef<ProcessedListingAddress[]>(address ?? []);

  const inferRequestIdRef = useRef(0);

  // True while the user has uncommitted typed text in the neighborhood field —
  // the async street→neighborhood fill must never stomp active typing.
  const neighborhoodTypingRef = useRef(false);

  useEffect(() => {
    if (isEqual(address ?? [], lastEmittedRef.current)) return;

    lastEmittedRef.current = address ?? [];
    setDraft(fromAddress(address));
  }, [address]);

  const commitDraft = (next: AddressDraft): void => {
    setDraft(next);

    const emitted = toAddress(next);
    lastEmittedRef.current = emitted;
    onAddressChange(emitted);
  };

  /**
   * Best-effort street → neighborhood auto-fill. Fires only when city + street
   * are present and the neighborhood is empty; a null inference fills nothing,
   * and the fill is skipped if the user touched any of the three fields while
   * the request was in flight.
   */
  const maybeInferNeighborhood = (next: AddressDraft): void => {
    const city = next.city.trim();
    const street = next.street.trim();

    if (!city || !street || next.neighborhood.trim()) return;

    const requestId = ++inferRequestIdRef.current;

    api
      .locationsStreetNeighborhood(city, street)
      .then(({ inference }) => {
        if (requestId !== inferRequestIdRef.current || !inference) return;

        const current = draftRef.current;

        const stillApplies =
          !neighborhoodTypingRef.current &&
          !current.neighborhood.trim() &&
          sameText(current.city, city) &&
          sameText(current.street, street);

        if (!stillApplies) return;

        commitDraft({
          ...current,
          neighborhood: inference.neighborhood,
          neighborhoodLocationId: inference.locationId,
          neighborhoodCity: current.city.trim()
        });
      })
      .catch(() => {
        // Inference is an enhancement — a miss must never block address entry.
      });
  };

  const applyCityChange = (params: {
    city: string;
    cityCode?: number;
    cityLocationId?: string;
    coords?: ProcessedListingAddressCoordinates;
  }): void => {
    const city = params.city.trim();
    const cityChanged = !sameText(city, draft.city);

    // Moving to a DIFFERENT city invalidates values known to belong to another
    // city (and stale coords). CLEARING the city (X / erased text) keeps
    // everything and only un-scopes future suggestions.
    const movingToOtherCity = cityChanged && city !== "";

    const clearNeighborhood =
      movingToOtherCity &&
      !!draft.neighborhood &&
      !!draft.neighborhoodCity &&
      !sameText(draft.neighborhoodCity, city);

    const clearStreet =
      movingToOtherCity && !!draft.street && !!draft.streetCity && !sameText(draft.streetCity, city);

    const next: AddressDraft = {
      ...draft,
      city,
      cityCode: params.cityCode,
      cityLocationId: params.cityLocationId,
      ...(clearNeighborhood
        ? { neighborhood: "", neighborhoodLocationId: undefined, neighborhoodCity: undefined }
        : {}),
      ...(clearStreet ? { street: "", streetLocationId: undefined, streetCity: undefined } : {}),
      coords: params.coords ?? (movingToOtherCity ? undefined : draft.coords)
    };

    commitDraft(next);

    // A kept street under a newly-set city can now be completed with its neighborhood.
    maybeInferNeighborhood(next);
  };

  const handleCityPick = (suggestion: LocationSuggestion): void => {
    applyCityChange({
      city: (suggestion.city ?? "").trim(),
      cityCode: suggestion.cityCode,
      cityLocationId: suggestion.locationId,
      coords: suggestion.coords
    });
  };

  const handleCityCommit = (text: string): void => {
    if (sameText(text, draft.city)) return;

    applyCityChange({ city: text });
  };

  const handleNeighborhoodPick = (suggestion: LocationSuggestion): void => {
    const next: AddressDraft = {
      ...draft,
      neighborhood: (suggestion.neighborhood ?? "").trim(),
      neighborhoodLocationId: suggestion.locationId,
      neighborhoodCity: suggestion.city?.trim() || draft.city.trim() || undefined
    };

    if (!draft.city.trim() && suggestion.city) {
      next.city = suggestion.city.trim();
      next.cityCode = suggestion.cityCode;
      next.cityLocationId = undefined;
    }

    if (suggestion.coords) next.coords = suggestion.coords;

    commitDraft(next);
  };

  const handleNeighborhoodCommit = (text: string): void => {
    if (sameText(text, draft.neighborhood)) return;

    commitDraft({
      ...draft,
      neighborhood: text,
      neighborhoodLocationId: undefined,
      neighborhoodCity: text ? draft.city.trim() || undefined : undefined
    });
  };

  const handleStreetPick = (suggestion: LocationSuggestion): void => {
    const next: AddressDraft = {
      ...draft,
      street: (suggestion.street ?? "").trim(),
      streetLocationId: suggestion.locationId,
      streetCity: suggestion.city?.trim() || draft.city.trim() || undefined
    };

    if (!draft.city.trim() && suggestion.city) {
      next.city = suggestion.city.trim();
      next.cityCode = suggestion.cityCode;
      next.cityLocationId = undefined;
    }

    if (suggestion.coords) next.coords = suggestion.coords;

    const expandedNeighborhood = suggestion.neighborhood?.trim();

    if (expandedNeighborhood) {
      // An expanded "street · neighborhood" option is an explicit choice of
      // both, so it may overwrite a previously-filled neighborhood.
      next.neighborhood = expandedNeighborhood;
      next.neighborhoodLocationId = undefined;
      next.neighborhoodCity = next.streetCity;
    }

    commitDraft(next);

    if (!expandedNeighborhood) maybeInferNeighborhood(next);
  };

  const handleStreetCommit = (text: string): void => {
    if (sameText(text, draft.street)) return;

    const next: AddressDraft = {
      ...draft,
      street: text,
      streetLocationId: undefined,
      streetCity: text ? draft.city.trim() || undefined : undefined
    };

    commitDraft(next);
    maybeInferNeighborhood(next);
  };

  // Recover the cityCode for a city that arrived as plain text (edit-mode seed
  // or free typing) so neighborhood/street suggestions can be scoped to it.
  // Silent: adopts only an exact-name match and never changes the visible text.
  useEffect(() => {
    const city = draft.city.trim();

    if (!city || draft.cityCode !== undefined) return;

    let cancelled = false;

    const timeoutId = setTimeout(async () => {
      try {
        const { suggestions } = await api.locationsAutocomplete(city, {
          resultType: "city",
          includeEmpty: true
        });

        if (cancelled) return;

        const exact = suggestions.find((suggestion) => sameText(suggestion.city, city));

        if (!exact) return;

        setDraft((prev) =>
          sameText(prev.city, city) && prev.cityCode === undefined
            ? { ...prev, cityCode: exact.cityCode }
            : prev
        );
      } catch {
        // Scoping is an enhancement — unscoped suggestions still work.
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.city, draft.cityCode]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <AddressAutocompleteField
          level="street"
          label={translate("listingsPage.addressPicker.street")}
          disabled={disabled}
          value={draft.street}
          cityCode={draft.cityCode}
          onPick={handleStreetPick}
          onCommitText={handleStreetCommit}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <AddressAutocompleteField
          level="neighborhood"
          label={translate("listingsPage.addressPicker.neighborhood")}
          disabled={disabled}
          value={draft.neighborhood}
          cityCode={draft.cityCode}
          onPick={handleNeighborhoodPick}
          onCommitText={handleNeighborhoodCommit}
          onTypingChange={(typing) => {
            neighborhoodTypingRef.current = typing;
          }}
          unmatchedHint={translate(
            "listingsPage.addressPicker.neighborhoodUnknown",
            "לא מצאנו שכונה בשם הזה — אפשר לבחור מהרשימה, או להשאיר ריק ונשלים אותה לפי הרחוב."
          )}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <AddressAutocompleteField
          level="city"
          label={translate("listingsPage.addressPicker.city")}
          required={required}
          disabled={disabled}
          value={draft.city}
          onPick={handleCityPick}
          onCommitText={handleCityCommit}
        />
      </Grid>
    </Grid>
  );
}

type AddressAutocompleteFieldProps = {
  level: AddressLevel;
  label: string;
  required?: boolean;
  disabled?: boolean;
  /** The committed value — the draft's text for this field. */
  value: string;
  /** Scope for neighborhood/street suggestion queries (absent = global). */
  cityCode?: number;
  onPick: (suggestion: LocationSuggestion) => void;
  /** Free-text commit (blur / Enter / clear-with-empty-string). */
  onCommitText: (text: string) => void;
  /** Reports whether the user currently has uncommitted typed text in this
   *  field — lets the parent hold off async fills that would stomp it. */
  onTypingChange?: (typing: boolean) => void;
  /**
   * Shown under the field when committed free text matches no place in the
   * registry. Only the NEIGHBORHOOD field passes one: city and street must stay
   * silent, since a genuinely new street is exactly what free text is for,
   * whereas an unrecognised neighborhood is better left empty — the server
   * fills it from the street. Measured 2026-09-10: ~1.4% of new realtor
   * listings carry a neighborhood the registry cannot place, and the field
   * accepted every one of them without a word.
   */
  unmatchedHint?: string;
};

/**
 * One freeSolo autocomplete over the canonical registry. Owns its own typing /
 * fetching state; the parent owns the committed value and all cross-field sync.
 */
function AddressAutocompleteField({
  level,
  label,
  required,
  disabled,
  value,
  cityCode,
  onPick,
  onCommitText,
  onTypingChange,
  unmatchedHint
}: AddressAutocompleteFieldProps): JSX.Element {
  const api = useWebotServer();

  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState<LocationSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  // True once committed free text has been checked against the registry and
  // matched nothing. Checked on COMMIT rather than read off `options`, which
  // can still be mid-debounce when the user tabs away — a hint that fires on a
  // stale fetch would tell people their real neighborhood is unknown.
  const [unmatched, setUnmatched] = useState(false);
  // Only user TYPING (onInputChange reason "input") updates the search query,
  // so programmatic input changes (pick, cross-fill) never trigger a refetch.
  const [searchQuery, setSearchQuery] = useState("");

  const requestIdRef = useRef(0);
  const focusedRef = useRef(false);

  // Adopt external commits (cross-fill, seed, cascade clear) into the input —
  // but never stomp text the user is actively typing in this field.
  useEffect(() => {
    if (focusedRef.current && inputValue.trim()) return;

    setInputValue(value);
    setSearchQuery("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const query = searchQuery.trim();

    if (query.length < 2) {
      requestIdRef.current += 1; // Invalidate any in-flight fetch.
      setOptions([]);
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;

    const timeoutId = setTimeout(async () => {
      try {
        setLoading(true);

        const { suggestions } = await api.locationsAutocomplete(query, {
          resultType: level,
          includeEmpty: true, // A 0-listing place is a valid listing address.
          includeCoords: true,
          cityCode,
          // Future server: one street option per neighborhood for
          // multi-neighborhood streets. Older deploys ignore the param.
          ...(level === "street" ? { expandStreetNeighborhoods: true } : {})
        });

        if (requestId !== requestIdRef.current) return;

        setOptions(suggestions);
      } catch (error) {
        console.error(`Location autocomplete failed (${level}):`, error);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, cityCode, level]);

  const handleInputChange = (
    _event: SyntheticEvent,
    newInputValue: string,
    reason: string
  ): void => {
    setInputValue(newInputValue);

    if (reason === "input") {
      setSearchQuery(newInputValue);
      onTypingChange?.(newInputValue.trim().length > 0);
    }
  };

  /**
   * Asks the registry whether committed free text names a real place, and shows
   * the hint when it does not. Silent for pickers with no hint (city, street),
   * and never blocks the commit — the value is already saved by the time this
   * resolves; the hint only invites a correction.
   */
  const checkUnmatched = async (text: string): Promise<void> => {
    if (!unmatchedHint) return;
    const trimmed = text.trim();
    if (!trimmed) {
      setUnmatched(false);
      return;
    }
    try {
      const { suggestions } = await api.locationsAutocomplete(trimmed, {
        resultType: level,
        includeEmpty: true,
        cityCode
      });
      setUnmatched(!suggestions.some((s) => sameText(s[level] ?? s.city, trimmed)));
    } catch {
      setUnmatched(false); // Never accuse the registry of a gap on a failed lookup.
    }
  };

  const handleChange = (
    _event: SyntheticEvent,
    newValue: LocationSuggestion | string | null
  ): void => {
    // Any selection/commit resolves whatever was being typed.
    onTypingChange?.(false);
    setSearchQuery("");

    if (newValue && typeof newValue === "object") {
      setUnmatched(false); // Picked from the registry — by definition known.
      onPick(newValue);
      return;
    }

    // Enter with free text (string) or the clear button (null).
    const text = typeof newValue === "string" ? newValue.trim() : "";
    onCommitText(text);
    void checkUnmatched(text);
  };

  const handleFocus = (): void => {
    focusedRef.current = true;
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>): void => {
    // Focus moving WITHIN the field (input → its clear button) is not a blur —
    // committing here would run the free-text path a beat before the clear.
    if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget as Node)) return;

    focusedRef.current = false;
    onTypingChange?.(false);

    // Free text commits on blur; unchanged text is a no-op.
    if (!sameText(inputValue, value)) {
      onCommitText(inputValue.trim());
      void checkUnmatched(inputValue);
    }
  };

  const getOptionLabel = (option: LocationSuggestion | string): string => {
    if (typeof option === "string") return option;

    return option[level] ?? option.city ?? "";
  };

  return (
    <Autocomplete
      freeSolo
      fullWidth
      disabled={disabled}
      options={options}
      filterOptions={(x) => x} // Server-filtered — never re-filter locally.
      value={value}
      inputValue={inputValue}
      getOptionLabel={getOptionLabel}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={unmatched ? unmatchedHint : undefined}
          FormHelperTextProps={{ sx: { color: "warning.dark" } }}
          inputProps={{
            ...params.inputProps,
            autoComplete: "new-password" // Kill browser address autofill.
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={18} />}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      renderOption={(props, option) => {
        // freeSolo widens the option type with string; real options are always
        // suggestion objects (strings only ever appear as the VALUE).
        const suggestion = typeof option === "string" ? undefined : option;

        const mainText = getOptionLabel(option);
        const textParts = parseSubstrings(mainText, matchSubstrings(mainText, inputValue));

        // A street option may carry its neighborhood (expandStreetNeighborhoods);
        // suggestions below city level always carry their city.
        const expandedNeighborhood = level === "street" ? suggestion?.neighborhood : undefined;
        const secondaryText = level === "city" ? undefined : suggestion?.city;

        return (
          <ListItem
            {...props}
            key={`${suggestion?.locationId ?? ""}__${suggestion?.neighborhood ?? ""}__${mainText}`}
          >
            <Box sx={{ wordWrap: "break-word" }}>
              {textParts.map(({ text, highlight }, index) => (
                <Typography
                  variant="body1"
                  component="span"
                  color="text.primary"
                  fontWeight={highlight ? "bold" : "regular"}
                  key={index}
                >
                  {text}
                </Typography>
              ))}
              {expandedNeighborhood && (
                <Typography variant="body1" component="span" color="text.secondary">
                  {` · ${expandedNeighborhood}`}
                </Typography>
              )}
              {secondaryText && (
                <Typography variant="body2" color="text.secondary">
                  {secondaryText}
                </Typography>
              )}
            </Box>
          </ListItem>
        );
      }}
    />
  );
}
